import { ArrowUpRight, FileText } from 'lucide-react';
import { useMemo } from 'react';

import { dateiAdresse } from '../directus/client';
import { Karte } from '../komponenten/Karte';
import { NochNichtsGepflegt } from '../komponenten/Zustaende';
import {
  ampelFuer,
  datumKurz,
  dringlichkeitsGruppe,
  tageBis,
  tageInWorten,
} from '../logik/ampel';
import type { DringlichkeitsGruppe } from '../logik/ampel';
import type { Dokument, Inhalte, Phase, Termin } from '../directus/typen';

interface TerminMitTagen {
  termin: Termin;
  tage: number;
}

const GRUPPEN: { key: DringlichkeitsGruppe; titel: string }[] = [
  { key: 'ueberfaellig', titel: 'Überfällig' },
  { key: 'dieseWoche', titel: 'Diese Woche' },
  { key: 'spaeter', titel: 'Später' },
];

const GRUPPEN_FARBE: Record<DringlichkeitsGruppe, string> = {
  ueberfaellig: 'bg-ampel-kritisch',
  dieseWoche: 'bg-ampel-nah',
  spaeter: 'bg-ampel-plan',
};

/**
 * Startbereich "Fristen": oben die naechste anstehende Frist als dunkler
 * Hero, darunter alle Termine gruppiert nach Dringlichkeit, darunter der
 * Schnellzugriff. Eine Ansicht fuer alle - keine Rollenfilterung mehr, siehe
 * README.
 */
export function Fristen({
  inhalte,
  phaseJetzt,
  heute,
}: {
  inhalte: Inhalte;
  phaseJetzt: Phase | null;
  heute: Date;
}) {
  // termine ist bereits chronologisch von Directus sortiert (sort=datum in
  // useInhalte); hier noch einmal ausdruecklich nach Taggen sortiert, damit
  // dieser Bereich sich nicht auf eine Annahme ueber eine andere Datei
  // verlassen muss.
  const mitTagen = useMemo<TerminMitTagen[]>(
    () =>
      inhalte.termine
        .map((termin) => ({ termin, tage: tageBis(termin.datum, heute) }))
        .sort((a, b) => a.tage - b.tage),
    [inhalte.termine, heute],
  );

  // Die zeitlich naechste Frist, die nicht ueberfaellig ist. Sind alle
  // Termine ueberfaellig, ist es die dringendste - und das ist zugleich die
  // erste Zeile der nach Tagen aufsteigend sortierten Liste (am staerksten
  // negativ, also am laengsten ueberfaellig).
  const naechste = mitTagen.find((e) => e.tage >= 0) ?? mitTagen[0] ?? null;

  const gruppen: Record<DringlichkeitsGruppe, TerminMitTagen[]> = {
    ueberfaellig: [],
    dieseWoche: [],
    spaeter: [],
  };
  for (const eintrag of mitTagen) gruppen[dringlichkeitsGruppe(eintrag.tage)].push(eintrag);

  // Dokumente, die zur laufenden Phase gehoeren, plus die "ganzjaehrigen"
  // ohne Phase - siehe Hilfetext an dokumente.phase in directus/modell.mjs.
  const schnellzugriff = inhalte.dokumente.filter(
    (d) => d.schnellzugriff && (d.phase === null || d.phase.id === phaseJetzt?.id),
  );

  return (
    <div className="space-y-6">
      {inhalte.termine.length === 0 ? (
        <NochNichtsGepflegt
          was="In den Terminen"
          hinweis="Sobald die Stufenleitung Fristen einträgt, erscheinen sie hier."
        />
      ) : (
        <>
          {naechste !== null && <HeroKarte eintrag={naechste} />}

          {GRUPPEN.map(({ key, titel }) => {
            const eintraege = gruppen[key];
            if (eintraege.length === 0) return null;
            return (
              <section key={key}>
                <div className="mb-2.5 flex items-center gap-2 px-1">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${GRUPPEN_FARBE[key]}`}
                    aria-hidden="true"
                  />
                  <span className="text-sm font-bold text-marke-tinte">{titel}</span>
                  <span className="text-sm text-marke-grau">{eintraege.length}</span>
                </div>
                <div className="space-y-2.5">
                  {eintraege.map(({ termin, tage }) => (
                    <FristenZeile key={termin.id} termin={termin} tage={tage} />
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}

      <section>
        <div className="mb-2.5 px-1 text-sm font-bold text-marke-tinte">Schnellzugriff</div>
        {schnellzugriff.length === 0 ? (
          <Karte className="p-6 text-center">
            <div className="text-sm text-marke-grau">
              Für die laufende Phase ist kein Dokument für den Schnellzugriff markiert.
            </div>
          </Karte>
        ) : (
          <div className="grid gap-2.5 sm:grid-cols-2 md:grid-cols-3">
            {schnellzugriff.map((d) => (
              <SchnellzugriffKarte key={d.id} dokument={d} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/** Ob sich fuer diesen Termin wirklich eine Datei oeffnen laesst - lieber
 *  kein Link als ein toter, siehe directus/beispieldaten.mjs. */
function kannOeffnen(termin: Termin): boolean {
  return termin.dokument !== null && termin.dokument.datei !== null;
}

function HeroKarte({ eintrag }: { eintrag: TerminMitTagen }) {
  const { termin, tage } = eintrag;
  return (
    <div className="rounded-3xl bg-marke-tinte p-5 text-white">
      <div className="text-xs font-semibold tracking-wide text-white/60 uppercase">
        Nächste Frist
      </div>
      <div className="mt-2 text-3xl leading-tight font-bold">{tageInWorten(tage)}</div>
      <div className="mt-2 text-base text-white/80">{termin.titel}</div>
      {termin.aktionstext !== null &&
        (kannOeffnen(termin) ? (
          <a
            href={dateiAdresse(termin.dokument!.datei!)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-marke-blau py-3.5 text-base font-semibold text-white transition-opacity hover:opacity-90"
            style={{ minHeight: 56 }}
          >
            {termin.aktionstext}
            <ArrowUpRight size={18} aria-hidden="true" />
          </a>
        ) : (
          <div
            className="mt-4 flex w-full items-center justify-center rounded-2xl bg-white/10 py-3.5 text-base font-semibold text-white/70"
            style={{ minHeight: 56 }}
          >
            Datei folgt
          </div>
        ))}
    </div>
  );
}

function FristenZeile({ termin, tage }: { termin: Termin; tage: number }) {
  const ampel = ampelFuer(tage);

  return (
    <Karte className="p-4">
      <div className="flex items-center gap-3" style={{ minHeight: 56 }}>
        <div className="min-w-0 flex-1">
          <div className="text-base font-semibold leading-snug text-marke-tinte">
            {termin.titel}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
            <span className={`font-medium ${ampel.textFarbe}`}>{tageInWorten(tage)}</span>
            <span className="text-marke-grau">· {datumKurz(termin.datum)}</span>
          </div>
        </div>
        {termin.aktionstext !== null &&
          (kannOeffnen(termin) ? (
            <a
              href={dateiAdresse(termin.dokument!.datei!)}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 rounded-xl bg-marke-blau px-3 py-2 text-xs font-semibold whitespace-nowrap text-white transition-opacity hover:opacity-90"
            >
              {termin.aktionstext}
            </a>
          ) : (
            <span className="shrink-0 rounded-xl bg-marke-flaeche px-3 py-2 text-xs font-medium whitespace-nowrap text-marke-grau">
              Datei folgt
            </span>
          ))}
      </div>
    </Karte>
  );
}

function SchnellzugriffKarte({ dokument }: { dokument: Dokument }) {
  const hatDatei = dokument.datei !== null;

  const inhalt = (
    <>
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          hatDatei ? 'bg-marke-blau/10 text-marke-blau' : 'bg-marke-flaeche text-marke-grau'
        }`}
      >
        <FileText size={19} aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-semibold text-marke-tinte">{dokument.titel}</div>
        {!hatDatei && <div className="text-xs text-marke-grau">PDF folgt</div>}
      </div>
    </>
  );

  if (hatDatei) {
    return (
      <a
        href={dateiAdresse(dokument.datei!)}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full items-center gap-3 rounded-2xl bg-white p-4 text-left transition-colors hover:bg-marke-flaeche"
        style={{ minHeight: 60 }}
      >
        {inhalt}
      </a>
    );
  }

  return (
    <div
      className="flex w-full items-center gap-3 rounded-2xl bg-white/60 p-4 text-left"
      style={{ minHeight: 60 }}
    >
      {inhalt}
    </div>
  );
}
